import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Center, Spinner, useToast } from '@chakra-ui/react';
import { QueryClient } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import { CDN_READY_COOKIE_NAME } from '@duke-hq/svc-files-lib/cookies';
import { trpcFiles } from './trpc';

export type AuthenticationStatus = 'pending' | 'authenticated' | 'failed';

function hasCdnReadyCookie(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.cookie.split('; ').some((c) => c.startsWith(`${CDN_READY_COOKIE_NAME}=`));
}

const CDN_CONNECT_MAX_ATTEMPTS = 4;
const CDN_CONNECT_BASE_DELAY_MS = 1000;

async function connectCdnSessionWithRetry(
  trpcClient: ReturnType<typeof trpcFiles.createClient>,
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= CDN_CONNECT_MAX_ATTEMPTS; attempt += 1) {
    try {
      await trpcClient.auth.connect.mutate();
      return;
    } catch (err) {
      lastError = err;
      if (attempt === CDN_CONNECT_MAX_ATTEMPTS) {
        break;
      }
      const delay = CDN_CONNECT_BASE_DELAY_MS * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

// 12h — half the 24h flag-cookie TTL (which is the tighter of the two
// server-side cookie lifetimes; the signed cookies live 48h).
const CDN_COOKIE_REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000;

interface FilesTrpcClientContextType {
  authenticationStatus: AuthenticationStatus;
  trpcClient: ReturnType<typeof trpcFiles.createClient>;
  queryClient: QueryClient;
  initialize: (options?: { silent?: boolean }) => Promise<void>;
}

const FilesTrpcClientContext = createContext<FilesTrpcClientContextType | null>(null);

export function FilesTrpcClientProvider({
  getAuthToken,
  children,
  enableCdnSession = false,
  filesServiceUrl,
  initialAuthenticationStatus,
}: {
  getAuthToken: () => Promise<{ token: string }>;
  children: ReactNode;
  enableCdnSession?: boolean;
  filesServiceUrl: string | undefined;
  /**
   * Initial auth status. When the server can read the `duke-cdn-ready` cookie
   * during SSR and pass `'authenticated'` here, the splash is skipped entirely
   * and the page renders content on first paint.
   */
  initialAuthenticationStatus?: AuthenticationStatus;
}) {
  if (filesServiceUrl == null) {
    return <>{children}</>;
  }

  return (
    <FilesTrpcClientProviderInner
      getAuthToken={getAuthToken}
      filesServiceUrl={filesServiceUrl}
      enableCdnSession={enableCdnSession}
      initialAuthenticationStatus={initialAuthenticationStatus}
    >
      {children}
    </FilesTrpcClientProviderInner>
  );
}

function FilesTrpcClientProviderInner({
  getAuthToken,
  children,
  filesServiceUrl,
  enableCdnSession,
  initialAuthenticationStatus,
}: {
  getAuthToken: () => Promise<{ token: string }>;
  children: ReactNode;
  filesServiceUrl: string;
  enableCdnSession: boolean;
  initialAuthenticationStatus?: AuthenticationStatus;
}) {
  // Optimistic initial state from SSR only. The lazy initialiser must be
  // pure and match what the server rendered — reading document.cookie here
  // would mismatch hydration whenever the browser has the cookie but the
  // SSR request didn't carry it (sibling tab set it, cookie-purge recovery,
  // etc.). The post-mount effect below bumps to 'authenticated' on next
  // tick if the cookie is actually present client-side.
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatus>(
    initialAuthenticationStatus ?? 'pending',
  );

  useEffect(() => {
    if (hasCdnReadyCookie()) {
      setAuthenticationStatus((prev) => (prev === 'pending' ? 'authenticated' : prev));
    }
  }, []);

  // The raw setter accepts any AuthenticationStatus, but no code path should
  // ever transition back to 'pending' after first render — doing so would
  // re-mount the splash and tear down whatever the user is doing. Narrow the
  // value signature here so the compiler refuses any future 'pending' call.
  // The cookie-check effect above uses the functional setter form, which is
  // correct by construction (returns only 'authenticated' or prev).
  const settle = useCallback((status: Exclude<AuthenticationStatus, 'pending'>) => {
    setAuthenticationStatus(status);
  }, []);

  const toast = useToast();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
          },
        },
      }),
  );

  const [trpcClient] = useState(() =>
    trpcFiles.createClient({
      links: [
        httpBatchLink({
          url: `${filesServiceUrl}/api/files`,
          transformer: superjson,
          fetch(url, options) {
            return fetch(url, { ...options, credentials: 'include' });
          },
        }),
      ],
    }),
  );

  const inFlightRef = useRef<{ promise: Promise<void>; silent: boolean } | undefined>(undefined);
  const initializedRef = useRef(false);

  // Consumers typically build `getAuthToken` from a tRPC mutation hook whose
  // return object isn't referentially stable, so the prop changes every
  // render. Wrap it in a ref-backed stable callback at the boundary so
  // `initialize` below depends only on stable values and can itself be
  // stable — effects that call it don't tear down on every render (which
  // would reset the 12h refresh interval to 0).
  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);
  const stableGetAuthToken = useCallback(() => getAuthTokenRef.current(), []);

  const initialize = useCallback(
    async (options: { silent?: boolean } = {}) => {
      // Wait for the handshake already running rather than reporting done
      // part way through it. Its body settles its own failures, so waiting on
      // it never rejects, and resolving does not imply success. A silent
      // handshake fails without a toast and leaves the status 'authenticated',
      // so a caller that is not silent runs its own handshake afterwards and
      // sees the failure itself.
      for (;;) {
        if (initializedRef.current) {
          return;
        }
        const inFlight = inFlightRef.current;
        if (inFlight == null) {
          break;
        }
        await inFlight.promise;
        if (!inFlight.silent || options.silent) {
          return;
        }
      }

      const handshake = (async () => {
        try {
          const { token } = await stableGetAuthToken();
          await trpcClient.auth.login.mutate({ token });
          await connectCdnSessionWithRetry(trpcClient);
          settle('authenticated');
          initializedRef.current = true;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to authenticate with files service';
          if (options.silent) {
            // Background re-init from the refresh path. Don't flip status to
            // 'failed' — that would tear down the refresh effect and leave the
            // user permanently stuck in degraded mode with no retry path. Keep
            // 'authenticated' so the next interval/visibility tick can try
            // again. Already-rendered images are unaffected by stale cookies.
            console.warn('files-service silent re-init failed', err);
          } else {
            settle('failed');
            toast({
              title: 'Authentication failed',
              description: message,
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        }
      })();

      inFlightRef.current = { promise: handshake, silent: options.silent === true };

      // Cleared from a microtask rather than inside the body, so it cannot run
      // before the assignment above — a body that threw before its first await
      // would otherwise leave the ref holding a settled promise forever.
      const clear = () => {
        inFlightRef.current = undefined;
      };
      void handshake.then(clear, clear);

      return handshake;
    },
    [trpcClient, stableGetAuthToken, toast, settle],
  );

  useEffect(() => {
    if (enableCdnSession) {
      // Reset the once-per-mount guard so that a logout-then-login cycle
      // within the same provider lifetime triggers a fresh handshake (new
      // file-service session keyed to the new user, fresh CDN cookies).
      initializedRef.current = false;
      void initialize();
    }
  }, [enableCdnSession, initialize]);

  // Refresh CDN cookies well before the 48h server-side TTL. The browser
  // doesn't refetch already-displayed images when cookies change, so this
  // doesn't cause UI flicker — it only matters for images requested after
  // the cookies would otherwise have expired.
  useEffect(() => {
    if (!enableCdnSession || authenticationStatus !== 'authenticated') {
      return;
    }

    // Shared timestamp so the interval and visibility-change handlers
    // coordinate — without this they can fire back-to-back when the user
    // returns to a tab right around the interval's natural firing time.
    let lastRefreshAt = Date.now();
    const refresh = () => {
      lastRefreshAt = Date.now();
      connectCdnSessionWithRetry(trpcClient).catch((err) => {
        // auth.connect can fail for two reasons:
        //   - Transient network: next interval/visibility tick will retry.
        //   - File-service session expired (30d TTL): connect won't ever
        //     succeed on its own. We need a full re-init via getAuthToken +
        //     auth.login + auth.connect, which only works if the upstream
        //     NextAuth session is still valid.
        // Cheap to retry the full init either way — a handshake already
        // running is shared rather than repeated. Pass silent so a transient
        // background failure doesn't toast on a working page; the user
        // would be confused by an error message they didn't trigger.
        console.warn('files-service refresh failed, escalating to re-init', err);
        initializedRef.current = false;
        void initialize({ silent: true });
      });
    };

    const interval = setInterval(refresh, CDN_COOKIE_REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (Date.now() - lastRefreshAt < CDN_COOKIE_REFRESH_INTERVAL_MS) {
        return;
      }
      refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enableCdnSession, authenticationStatus, trpcClient, initialize]);

  // Gate the rest of the tree on auth status. While 'pending', show a splash
  // so no file-bearing UI renders before cookies are set — eliminates the
  // race for every consumer (tiptap, avatars, anything pointing at
  // file.duke.co) without per-component code. On 'failed' we render children
  // anyway in degraded mode rather than trapping the user on the splash.
  if (enableCdnSession && authenticationStatus === 'pending') {
    return (
      <Center w="100vw" h="100vh" data-testid="files-auth-splash">
        <Spinner aria-label="Loading" />
      </Center>
    );
  }

  return (
    <FilesTrpcClientContext.Provider
      value={{ authenticationStatus, trpcClient, queryClient, initialize }}
    >
      {children}
    </FilesTrpcClientContext.Provider>
  );
}

export function useFilesTrpcClient(): FilesTrpcClientContextType {
  const context = useContext(FilesTrpcClientContext);
  if (!context) {
    throw new Error(
      'useFilesTrpcClient requires FilesTrpcClientProvider to be mounted with a filesServiceUrl',
    );
  }
  return context;
}
